import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import Link from 'next/link';
import { FileText, Search, Users, Award, Handshake, ArrowRight } from 'lucide-react';
import { IconCard } from '../IconCard';
import { CAREERS_COPY } from '../../../constants/careersTranslations';

const STEP_ICONS = [FileText, Search, Users, Award, Handshake];

export function CareersProcessSection({ lang = 'en', copy }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const t = copy?.process ?? CAREERS_COPY[lang]?.process ?? CAREERS_COPY.en.process;
  const steps = t.steps ?? [];

  return (
    <section ref={ref} className="section-rhythm bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 min-w-0">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="heading-2 mb-12 text-gray-900 text-center"
        >
          {t.title}
        </motion.h2>
        <div className="space-y-8">
          {steps.map((step, index) => {
            const Icon = STEP_ICONS[index];
            return (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 24 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 + index * 0.08 }}
                className="group flex gap-6 p-6 rounded-2xl border border-gray-200 bg-gray-50/50 hover:bg-gray-50 transition-all"
              >
                <div className="flex items-center shrink-0 text-2xl font-bold text-gray-300 tabular-nums">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                  { step.additional_text ? <p className="text-gray-600 leading-relaxed">{step.additional_text}</p> : null }
                </div>
                {Icon && (
                  <div className="shrink-0">
                    <IconCard Icon={Icon} />
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
        {t.linkHref && t.linkText && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-center mt-10"
          >
            <Link
              href={t.linkHref}
              className="inline-flex items-center gap-2 text-brand font-semibold hover:gap-3 transition-all"
            >
              {t.linkText}
              <ArrowRight className="w-4 h-4 shrink-0" />
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
}
